import { Loader2 } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import { api } from "convex/_generated/api";
import { useWorkspaceQuery } from "@/hooks";

/**
 * Format a timestamp to a human-readable relative time string.
 */
function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return days === 1 ? "1 day ago" : `${days} days ago`;
  }
  if (hours > 0) {
    return hours === 1 ? "1 hr ago" : `${hours} hrs ago`;
  }
  if (minutes > 0) {
    return minutes === 1 ? "1 min ago" : `${minutes} min ago`;
  }
  return "Just now";
}

/**
 * Get initials from a name.
 */
function getInitials(name: string | null): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Generate a consistent color based on a string.
 */
function getColorFromString(str: string): string {
  const colors = [
    "#3b82f6", // blue
    "#10b981", // green
    "#8b5cf6", // purple
    "#f59e0b", // amber
    "#f43f5e", // rose
    "#06b6d4", // cyan
    "#a855f7", // violet
    "#ec4899", // pink
  ];
  const hash = str.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
}

/**
 * Map activity type to action verb.
 */
function getActionFromType(type: string): string {
  const actionMap: Record<string, string> = {
    created: "created audit",
    update: "updated audit",
    status: "changed status",
    lifecycle: "updated audit status",
    summary: "generated summary",
    checklist: "updated checklist",
    failure: "encountered error",
  };
  return actionMap[type] || "performed action";
}

export function TeamActivity() {
  const { t } = useTranslation(["dashboard"]);

  // Fetch activities from all user's audits
  const { data: activities, isLoading } = useWorkspaceQuery(
    api.audit_queries.getAllUserAuditActivities,
    {
      limit: 10,
      hoursBack: 24 * 7, // Last 7 days
    },
  );

  return (
    <div className="rounded-xl bg-card border border-border overflow-hidden h-full flex flex-col">
      <div className="px-4 pt-4 pb-2">
        <h3 className="text-sm font-semibold text-primary">
          {t("dashboard:teamActivity.title")}
        </h3>
      </div>
      <div className="px-4 pb-4 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted" />
          </div>
        ) : !activities || activities.length === 0 ? (
          <div className="py-4 text-center text-muted text-sm">
            No recent activity
          </div>
        ) : (
          activities.map((item) => (
            <div
              key={`${item.id}-${item.timestamp}`}
              className="flex gap-2.5 py-2 border-b border-border last:border-0"
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0"
                style={{
                  background: getColorFromString(item.userName || "Unknown"),
                }}
              >
                {getInitials(item.userName)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs leading-relaxed">
                  <strong className="font-semibold text-primary">
                    {item.userName || "System"}
                  </strong>{" "}
                  <span className="text-secondary">
                    {getActionFromType(item.type)}
                  </span>
                </div>
                <div className="text-xs text-blue-500 font-medium mt-0.5 truncate">
                  {item.auditName}
                </div>
                <div className="text-[10px] text-muted mt-0.5">
                  {formatRelativeTime(item.timestamp)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
